/**
 * AIModelGovernancePanel — Firm-wide LLM model governance inside EdgeLex settings.
 *
 * Uses the D2.11 governance foundation API:
 * - GET /api/v1/governance/providers (admin)
 * - GET /api/v1/governance/models (admin)
 * - GET /api/v1/governance/my-policy (user-safe, shows effective policy)
 * - GET /api/v1/governance/realm-policies (admin)
 * - POST /api/v1/governance/realm-policies (admin)
 * - DELETE /api/v1/governance/realm-policies/:id (admin, realm-scoped)
 * - GET /api/v1/governance/usage/realm-summary (admin)
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import AddProviderModal from './AddProviderModal';
import EditProviderModal from './EditProviderModal';
import {
  PlusIcon,
  CpuChipIcon,
  ServerIcon,
  ChartBarIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  XCircleIcon,
  StarIcon,
  CloudIcon,
  ServerStackIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  ArrowTrendingUpIcon,
  FunnelIcon,
  ArrowsUpDownIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { getAIProviderIcon } from '@/components/icons/techIconMap';
import apiClient from '@/api/axios';

// ── Types ──

interface Provider {
  id: string;
  provider_key: string;
  display_name: string;
  provider_type: string;
  status: string;
}

interface Model {
  id: string;
  model_key: string;
  display_name: string;
  capabilities: string[];
  status: string;
  max_context_tokens: number | null;
  cost_per_1k_input: number | null;
  cost_per_1k_output: number | null;
  provider?: Provider;
}

interface RealmPolicy {
  id: string;
  realm_id: string;
  provider_id: string | null;
  model_id: string | null;
  decision: string;
  is_realm_default: boolean;
  local_models_enabled: boolean;
  frontier_models_enabled: boolean;
  status: string;
  notes: string | null;
}

interface UsageSummary {
  realmId: string;
  totalEvents: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  totalEstimatedCost: number;
  byProvider: Record<string, { events: number; tokens: number; cost: number }>;
  byModel: Record<string, { events: number; tokens: number; cost: number }>;
}

interface EffectivePolicy {
  allowedModels: Array<{ modelId: string; modelKey: string; displayName: string; providerKey: string; isDefault: boolean }>;
  deniedModels: Array<{ modelId: string; modelKey: string; reason: string }>;
}

interface ModelRateCard {
  modelKey: string;
  displayName: string;
  providerKey: string;
  providerName: string;
  costPer1kInput: number | null;
  costPer1kOutput: number | null;
  maxContextTokens: number | null;
  pricingUpdatedAt: string | null;
}

interface EnhancedUsageSummary {
  realmId: string;
  periodStart: string | null;
  periodEnd: string | null;
  totalEvents: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  totalEstimatedCost: number;
  byProvider: Record<string, { events: number; inputTokens: number; outputTokens: number; tokens: number; cost: number }>;
  byModel: Record<string, { events: number; inputTokens: number; outputTokens: number; tokens: number; cost: number; providerKey: string }>;
  byFeatureSurface: Record<string, { events: number; tokens: number; cost: number }>;
  byUser: Array<{ userId: string; displayName: string; email: string; events: number; tokens: number; cost: number; topModel: string }>;
  byMatter: Array<{ matterId: string; matterTitle: string; matterNumber: string; clientName: string; events: number; tokens: number; cost: number }>;
  unboundCost: number;
  dailyBreakdown: Array<{ date: string; events: number; inputTokens: number; outputTokens: number; cost: number }>;
  modelRateCards: Record<string, ModelRateCard>;
}

type TimePeriod = 'today' | '7d' | '30d' | 'all';

const PROVIDER_TOP_UP_URLS: Record<string, string> = {
  openai: 'https://platform.openai.com/settings/organization/billing/overview',
  anthropic: 'https://platform.claude.com/settings/billing',
  nvidia: 'https://build.nvidia.com/settings/api-keys',
  moonshot: 'https://platform.kimi.ai/console/pay',
};

function getProviderTopUpUrl(provider: Provider): string | null {
  if (provider.provider_key in PROVIDER_TOP_UP_URLS) {
    return PROVIDER_TOP_UP_URLS[provider.provider_key];
  }

  const displayName = provider.display_name.toLowerCase();
  if (displayName.includes('nvidia')) {
    return PROVIDER_TOP_UP_URLS.nvidia;
  }
  if (displayName.includes('moonshot') || displayName.includes('kimi')) {
    return PROVIDER_TOP_UP_URLS.moonshot;
  }

  return null;
}

// ── Tab types ──

type GovTab = 'providers' | 'policies' | 'usage';

// ── Component ──

const AIModelGovernancePanel: React.FC = () => {
  const [activeTab, setActiveTab] = useState<GovTab>('providers');
  const [showAddProvider, setShowAddProvider] = useState(false);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [realmPolicies, setRealmPolicies] = useState<RealmPolicy[]>([]);
  const [effectivePolicy, setEffectivePolicy] = useState<EffectivePolicy | null>(null);
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [enhancedUsage, setEnhancedUsage] = useState<EnhancedUsageSummary | null>(null);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all');
  const [modelSortField, setModelSortField] = useState<'cost' | 'events' | 'tokens' | 'efficiency'>('cost');
  const [modelSortAsc, setModelSortAsc] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set());
  const [editingProvider, setEditingProvider] = useState<Provider | null>(null);

  // ── Data fetching ──

  const getSinceDate = useCallback((period: TimePeriod): string | undefined => {
    if (period === 'all') return undefined;
    const now = new Date();
    if (period === 'today') {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      return d.toISOString();
    }
    if (period === '7d') {
      now.setDate(now.getDate() - 7);
      return now.toISOString();
    }
    if (period === '30d') {
      now.setDate(now.getDate() - 30);
      return now.toISOString();
    }
    return undefined;
  }, []);

  // Fetch governance config (providers, models, policies) — only on mount
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [provRes, modRes, polRes, effRes] = await Promise.all([
        apiClient.get('/governance/providers'),
        apiClient.get('/governance/models'),
        apiClient.get('/governance/realm-policies'),
        apiClient.get('/governance/realm-effective-policy'),
      ]);
      setProviders(provRes.data);
      setModels(modRes.data);
      setRealmPolicies(polRes.data);
      setEffectivePolicy(effRes.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load governance data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch usage data — runs when timePeriod changes, without clearing the screen
  const [usageLoading, setUsageLoading] = useState(false);
  const fetchUsage = useCallback(async () => {
    setUsageLoading(true);
    try {
      const since = getSinceDate(timePeriod);
      const usageParams = since ? `?since=${encodeURIComponent(since)}` : '';
      const [usageRes, enhancedRes] = await Promise.all([
        apiClient.get(`/governance/usage/realm-summary${usageParams}`),
        apiClient.get(`/governance/usage/enhanced-summary${usageParams}`),
      ]);
      setUsageSummary(usageRes.data);
      setEnhancedUsage(enhancedRes.data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load usage data');
    } finally {
      setUsageLoading(false);
    }
  }, [timePeriod, getSinceDate]);

  // Combined fetch for policy actions (e.g. after toggling a model)
  const fetchAll = useCallback(async () => {
    await Promise.all([fetchConfig(), fetchUsage()]);
  }, [fetchConfig, fetchUsage]);

  // Initial load: config + usage
  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => { fetchUsage(); }, [fetchUsage]);

  // ── Derived state ──

  const providerClassPolicies = realmPolicies.filter((policy) => !policy.provider_id && !policy.model_id);

  const localEnabled =
    providerClassPolicies.length === 0 ||
    providerClassPolicies.every((policy) => policy.local_models_enabled);
  const frontierEnabled =
    providerClassPolicies.length === 0 ||
    providerClassPolicies.every((policy) => policy.frontier_models_enabled);

  const defaultModel = effectivePolicy?.allowedModels.find((m) => m.isDefault) || null;
  const langGraphAllowedModelCount = effectivePolicy?.allowedModels.length ?? 0;
  const langGraphDefaultModelLabel = defaultModel
    ? `${defaultModel.displayName} (${defaultModel.providerKey})`
    : 'No default model selected';

  // ── Policy actions ──

  const toggleProviderType = useCallback(async (type: 'local' | 'frontier', enabled: boolean) => {
    setSaving(true);
    try {
      // Remove existing type-control policies first
      for (const p of realmPolicies.filter((rp) => !rp.provider_id && !rp.model_id)) {
        await apiClient.delete(`/governance/realm-policies/${p.id}`);
      }
      // Create new policy with updated flags
      await apiClient.post('/governance/realm-policies', {
        decision: 'allow',
        localModelsEnabled: type === 'local' ? enabled : localEnabled,
        frontierModelsEnabled: type === 'frontier' ? enabled : frontierEnabled,
        notes: `${type} models ${enabled ? 'enabled' : 'disabled'} by admin`,
      });
      await fetchAll();
    } catch (e: any) {
      setError(e?.message || 'Failed to update policy');
    } finally {
      setSaving(false);
    }
  }, [realmPolicies, localEnabled, frontierEnabled, fetchAll]);

  const toggleModelDeny = useCallback(async (modelId: string, deny: boolean) => {
    setSaving(true);
    try {
      // Remove existing model-specific policies
      for (const p of realmPolicies.filter((rp) => rp.model_id === modelId)) {
        await apiClient.delete(`/governance/realm-policies/${p.id}`);
      }
      // Explicit policy required in both directions (default-deny means no policy = denied)
      await apiClient.post('/governance/realm-policies', {
        modelId,
        decision: deny ? 'deny' : 'allow',
        notes: deny ? 'Model denied by admin' : 'Model allowed by admin',
      });
      await fetchAll();
    } catch (e: any) {
      setError(e?.message || 'Failed to update model policy');
    } finally {
      setSaving(false);
    }
  }, [realmPolicies, fetchAll]);

  const setDefaultModel = useCallback(async (modelId: string) => {
    setSaving(true);
    try {
      // Remove existing default policies
      for (const p of realmPolicies.filter((rp) => rp.is_realm_default)) {
        await apiClient.delete(`/governance/realm-policies/${p.id}`);
      }
      await apiClient.post('/governance/realm-policies', {
        modelId,
        decision: 'allow',
        isRealmDefault: true,
        notes: 'Set as firm default by admin',
      });
      await fetchAll();
    } catch (e: any) {
      setError(e?.message || 'Failed to set default model');
    } finally {
      setSaving(false);
    }
  }, [realmPolicies, fetchAll]);

  const toggleExpanded = useCallback((providerId: string) => {
    setExpandedProviders((prev) => {
      const next = new Set(prev);
      if (next.has(providerId)) next.delete(providerId);
      else next.add(providerId);
      return next;
    });
  }, []);

  // ── Loading ──

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 rounded-xl bg-[var(--brand-surface-secondary)] animate-pulse" />
        ))}
      </div>
    );
  }

  // ── Tab content ──

  const TABS: { key: GovTab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { key: 'providers', label: 'Providers & Models', icon: CpuChipIcon },
    { key: 'policies', label: 'Firm Policy', icon: ShieldCheckIcon },
    { key: 'usage', label: 'Usage & Accounting', icon: ChartBarIcon },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-[var(--brand-text-primary)]">AI Model Governance</h2>
          <p className="text-sm text-[var(--brand-text-tertiary)] mt-1">
            Control which AI models LangGraph Lex Runtime can use, set defaults, and monitor usage.
          </p>
        </div>
        <button onClick={() => setShowAddProvider(true)}
          className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[var(--brand-accent-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity">
          <PlusIcon className="w-4 h-4" /> Add Provider
        </button>
      </div>

      <AddProviderModal open={showAddProvider} onClose={() => setShowAddProvider(false)} onSaved={fetchAll} />

      <div className="mb-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50/70 dark:bg-emerald-900/15 p-4">
          <div className="flex items-start gap-3">
            <ShieldCheckIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                  LangGraph Runtime Governance
                </h3>
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300">
                  Active authority
                </span>
              </div>
              <p className="text-sm text-emerald-800 dark:text-emerald-200 mt-1">
                Lex Runtime uses EdgeLex DB policy before any model call. Browser clients cannot override this policy.
              </p>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div className="rounded-lg bg-white/70 dark:bg-black/10 px-3 py-2">
                  <div className="text-emerald-700 dark:text-emerald-300 font-medium">Allowed models</div>
                  <div className="text-emerald-950 dark:text-emerald-50">{langGraphAllowedModelCount}</div>
                </div>
                <div className="rounded-lg bg-white/70 dark:bg-black/10 px-3 py-2">
                  <div className="text-emerald-700 dark:text-emerald-300 font-medium">Default model</div>
                  <div className="text-emerald-950 dark:text-emerald-50 truncate">{langGraphDefaultModelLabel}</div>
                </div>
                <div className="rounded-lg bg-white/70 dark:bg-black/10 px-3 py-2">
                  <div className="text-emerald-700 dark:text-emerald-300 font-medium">External runtime authority</div>
                  <div className="text-emerald-950 dark:text-emerald-50">No</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50/70 dark:bg-amber-900/15 p-4">
          <div className="flex items-start gap-3">
            <ClockIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-100">Provider Setup Status</h3>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                Add/Edit Provider no longer calls the removed foreground-runtime sync path. Provider onboarding now belongs in EdgeLex governance.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-[var(--brand-surface-secondary)] mb-6 w-fit">
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === key
                ? 'bg-[var(--brand-surface-primary)] text-[var(--brand-accent-primary)] shadow-sm'
                : 'text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)]'
            }`}>
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ══ Providers & Models Tab ══ */}
      {activeTab === 'providers' && (
        <div className="space-y-6">
          {providers.map((provider) => {
            const providerModels = models.filter((m) => m.provider?.id === provider.id);
            const isLocal = provider.provider_type === 'local';
            const typeEnabled = isLocal ? localEnabled : frontierEnabled;
            const isExpanded = expandedProviders.has(provider.id);
            const allowedCount = providerModels.filter((m) => effectivePolicy?.allowedModels.find((a) => a.modelId === m.id)).length;
            const topUpUrl = getProviderTopUpUrl(provider);

            return (
              <div key={provider.id} className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-primary)] overflow-hidden">
                {/* Provider header */}
                <div
                  className="flex items-center justify-between px-5 py-4 bg-[var(--brand-surface-secondary)]/50 cursor-pointer select-none hover:bg-[var(--brand-surface-secondary)]/80 transition-colors"
                  onClick={() => toggleExpanded(provider.id)}
                >
                  <div className="flex items-center gap-3">
                    {isExpanded
                      ? <ChevronDownIcon className="w-4 h-4 text-[var(--brand-text-tertiary)] flex-shrink-0" />
                      : <ChevronRightIcon className="w-4 h-4 text-[var(--brand-text-tertiary)] flex-shrink-0" />}
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      isLocal ? 'bg-emerald-50 dark:bg-emerald-900/20' : 'bg-blue-50 dark:bg-blue-900/20'
                    }`}>
                      {(() => {
                        const BrandIcon = getAIProviderIcon(provider.provider_key);
                        if (BrandIcon) return <BrandIcon className="w-5 h-5" />;
                        return isLocal
                          ? <ServerStackIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                          : <CloudIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />;
                      })()}
                    </div>
                    <div>
                      <h3 className="font-semibold text-[var(--brand-text-primary)]">{provider.display_name}</h3>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                          isLocal
                            ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300'
                            : 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        }`}>
                          {isLocal ? 'Local' : 'Frontier'}
                        </span>
                        <span className={`text-xs ${provider.status === 'active' ? 'text-green-600' : 'text-[var(--brand-text-tertiary)]'}`}>
                          {provider.status}
                        </span>
                        <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300">
                          LangGraph governed
                        </span>
                        <span className="text-xs text-[var(--brand-text-tertiary)]">
                          {allowedCount}/{providerModels.length} models enabled
                        </span>
                        {topUpUrl && (
                          <a
                            href={topUpUrl}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-[var(--brand-accent-primary)] hover:underline"
                            title={`Open ${provider.display_name} billing / top-up page`}
                          >
                            Add Credits
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!typeEnabled && (
                      <span className="text-xs font-medium text-red-500 bg-red-50 dark:bg-red-900/20 px-2 py-1 rounded">
                        {isLocal ? 'Local' : 'Frontier'} models disabled
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingProvider(provider); }}
                      className="p-1.5 rounded-lg hover:bg-[var(--brand-surface-secondary)] text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)] transition-colors"
                      title="Configure provider"
                    >
                      <Cog6ToothIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                {/* Models — collapsed by default */}
                {isExpanded && (
                <div className="divide-y divide-[var(--brand-border)] border-t border-[var(--brand-border)]">
                  {topUpUrl && (
                    <div className="px-5 py-3 bg-[var(--brand-surface-secondary)]/30 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-[var(--brand-text-primary)]">Billing / Top-Up</div>
                        <div className="text-xs text-[var(--brand-text-tertiary)] truncate">{topUpUrl}</div>
                      </div>
                      <a
                        href={topUpUrl}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[var(--brand-border)] text-xs font-medium text-[var(--brand-accent-primary)] hover:bg-[var(--brand-surface-secondary)] transition-colors"
                      >
                        Open Billing
                      </a>
                    </div>
                  )}
                  {providerModels.map((model) => {
                    const allowed = effectivePolicy?.allowedModels.find((m) => m.modelId === model.id);
                    const denied = effectivePolicy?.deniedModels.find((m) => m.modelId === model.id);
                    const isDefault = allowed?.isDefault ?? false;
                    const isAllowed = !!allowed;

                    return (
                      <div key={model.id} className={`flex items-center justify-between px-5 py-3 ${!typeEnabled ? 'opacity-50' : ''}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isAllowed ? 'bg-green-500' : 'bg-red-400'}`} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-[var(--brand-text-primary)]">{model.display_name}</span>
                              {isDefault && (
                                <span className="flex items-center gap-0.5 text-xs text-amber-600 dark:text-amber-400">
                                  <StarIcon className="w-3 h-3" /> Default
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mt-0.5">
                              <span className="text-xs text-[var(--brand-text-tertiary)] font-mono">{model.model_key}</span>
                              {model.capabilities?.map((cap) => (
                                <span key={cap} className="text-xs bg-[var(--brand-surface-secondary)] text-[var(--brand-text-tertiary)] px-1 rounded">
                                  {cap}
                                </span>
                              ))}
                              {model.max_context_tokens && (
                                <span className="text-xs text-[var(--brand-text-tertiary)]">
                                  {(model.max_context_tokens / 1000).toFixed(0)}K ctx
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 flex-shrink-0">
                          {typeEnabled && (
                            <>
                              <button
                                onClick={() => setDefaultModel(model.id)}
                                disabled={saving || isDefault || !isAllowed}
                                className={`text-xs px-2 py-1 rounded transition-colors ${
                                  isDefault
                                    ? 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
                                    : 'text-[var(--brand-text-tertiary)] hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/10'
                                } disabled:opacity-50`}
                                title={isDefault ? 'Current default' : 'Set as default'}
                              >
                                <StarIcon className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => toggleModelDeny(model.id, isAllowed)}
                                disabled={saving}
                                className={`text-xs px-2.5 py-1 rounded font-medium transition-colors ${
                                  isAllowed
                                    ? 'text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/20 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20 dark:hover:text-red-300'
                                    : 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 hover:bg-green-50 hover:text-green-700 dark:hover:bg-green-900/20 dark:hover:text-green-300'
                                }`}
                              >
                                {isAllowed ? 'Allowed' : 'Denied'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Edit Provider Modal */}
      {editingProvider && (
        <EditProviderModal
          open={!!editingProvider}
          provider={editingProvider}
          onClose={() => setEditingProvider(null)}
          onSaved={fetchAll}
        />
      )}

      {/* ══ Firm Policy Tab ══ */}
      {activeTab === 'policies' && (
        <div className="space-y-6">
          {/* Provider type toggles */}
          <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-primary)] p-5">
            <h3 className="text-sm font-semibold text-[var(--brand-text-secondary)] uppercase tracking-wider mb-4">
              Model Class Controls
            </h3>
            <p className="text-sm text-[var(--brand-text-tertiary)] mb-4">
              These controls are enforced by EdgeLex policy in the LangGraph Lex Runtime path.
            </p>
            <div className="space-y-4">
              {([
                { type: 'local' as const, label: 'Local Models', desc: 'Models running on your own infrastructure (ai01)', icon: ServerStackIcon, enabled: localEnabled, color: 'emerald' },
                { type: 'frontier' as const, label: 'Frontier Models', desc: 'Cloud-hosted models from OpenAI, Anthropic, etc.', icon: CloudIcon, enabled: frontierEnabled, color: 'blue' },
              ]).map(({ type, label, desc, icon: Icon, enabled, color }) => (
                <div key={type} className="flex items-center justify-between p-4 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-surface-secondary)]/30">
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 text-${color}-600 dark:text-${color}-400`} />
                    <div>
                      <p className="text-sm font-medium text-[var(--brand-text-primary)]">{label}</p>
                      <p className="text-xs text-[var(--brand-text-tertiary)]">{desc}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleProviderType(type, !enabled)}
                    disabled={saving}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      enabled ? 'bg-[var(--brand-accent-primary)]' : 'bg-gray-300 dark:bg-gray-600'
                    } disabled:opacity-50`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Effective policy summary */}
          {effectivePolicy && (
            <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-primary)] p-5">
              <h3 className="text-sm font-semibold text-[var(--brand-text-secondary)] uppercase tracking-wider mb-4">
                Effective Policy Summary
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-green-600 dark:text-green-400 mb-2 flex items-center gap-1">
                    <CheckCircleIcon className="w-3.5 h-3.5" /> Allowed Models ({effectivePolicy.allowedModels.length})
                  </p>
                  <div className="space-y-1">
                    {effectivePolicy.allowedModels.map((m) => (
                      <div key={m.modelId} className="flex items-center gap-2 text-sm text-[var(--brand-text-secondary)]">
                        <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                        <span>{m.displayName}</span>
                        <span className="text-xs text-[var(--brand-text-tertiary)]">({m.providerKey})</span>
                        {m.isDefault && <StarIcon className="w-3 h-3 text-amber-500" />}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-medium text-red-600 dark:text-red-400 mb-2 flex items-center gap-1">
                    <XCircleIcon className="w-3.5 h-3.5" /> Denied Models ({effectivePolicy.deniedModels.length})
                  </p>
                  {effectivePolicy.deniedModels.length === 0 ? (
                    <p className="text-sm text-[var(--brand-text-tertiary)]">None</p>
                  ) : (
                    <div className="space-y-1">
                      {effectivePolicy.deniedModels.map((m) => (
                        <div key={m.modelId} className="flex items-center gap-2 text-sm text-[var(--brand-text-secondary)]">
                          <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
                          <span>{m.modelKey}</span>
                          <span className="text-xs text-[var(--brand-text-tertiary)]">— {m.reason}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {defaultModel && (
                <div className="mt-4 pt-4 border-t border-[var(--brand-border)]">
                  <p className="text-xs text-[var(--brand-text-tertiary)]">
                    Firm default: <span className="font-medium text-[var(--brand-text-primary)]">{defaultModel.displayName}</span>
                    <span className="text-[var(--brand-text-tertiary)]"> ({defaultModel.providerKey})</span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ══ Usage & Accounting Tab ══ */}
      {activeTab === 'usage' && enhancedUsage && (() => {
        const eu = enhancedUsage;
        const totalCost = eu.totalEstimatedCost;

        // Sort models
        const sortedModels = Object.entries(eu.byModel)
          .map(([key, data]) => {
            const rate = eu.modelRateCards[key];
            const costPerRequest = data.events > 0 ? data.cost / data.events : 0;
            const costPer1kOut = data.outputTokens > 0 ? (data.cost / data.outputTokens) * 1000 : 0;
            return { key, ...data, costPerRequest, costPer1kOut, rate };
          })
          .sort((a, b) => {
            let av: number, bv: number;
            switch (modelSortField) {
              case 'cost': av = a.cost; bv = b.cost; break;
              case 'events': av = a.events; bv = b.events; break;
              case 'tokens': av = a.tokens; bv = b.tokens; break;
              case 'efficiency': av = a.costPer1kOut; bv = b.costPer1kOut; break;
              default: av = a.cost; bv = b.cost;
            }
            return modelSortAsc ? av - bv : bv - av;
          });

        // Daily breakdown for burn rate
        const days = eu.dailyBreakdown;
        const avgDailyCost = days.length > 0 ? days.reduce((s, d) => s + d.cost, 0) / days.length : 0;
        const projectedMonthlyCost = avgDailyCost * 30;

        // Feature surface labels
        const featureSurfaceLabels: Record<string, string> = {
          chat: 'User Chat (Direct)',
          chat_coordinated: 'User Chat (Coordinated)',
          agent: 'Agent Runs',
          unknown: 'Unattributed',
        };

        const toggleSort = (field: typeof modelSortField) => {
          if (modelSortField === field) setModelSortAsc(!modelSortAsc);
          else { setModelSortField(field); setModelSortAsc(false); }
        };

        const SortHeader: React.FC<{ field: typeof modelSortField; label: string; className?: string }> = ({ field, label, className }) => (
          <button onClick={() => toggleSort(field)}
            className={`flex items-center gap-0.5 text-xs font-medium uppercase tracking-wider hover:text-[var(--brand-text-primary)] transition-colors ${
              modelSortField === field ? 'text-[var(--brand-accent-primary)]' : 'text-[var(--brand-text-tertiary)]'
            } ${className || ''}`}>
            {label}
            {modelSortField === field && (
              <ArrowsUpDownIcon className="w-3 h-3" />
            )}
          </button>
        );

        return (
        <div className="space-y-6">
          {/* ── Time Period Filter ── */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FunnelIcon className="w-4 h-4 text-[var(--brand-text-tertiary)]" />
              <span className="text-sm text-[var(--brand-text-tertiary)]">Period:</span>
              {usageLoading && (
                <div className="w-4 h-4 border-2 border-[var(--brand-accent-primary)] border-t-transparent rounded-full animate-spin" />
              )}
              <div className="flex gap-1 p-0.5 rounded-lg bg-[var(--brand-surface-secondary)]">
                {([
                  { key: 'today' as TimePeriod, label: 'Today' },
                  { key: '7d' as TimePeriod, label: '7 Days' },
                  { key: '30d' as TimePeriod, label: '30 Days' },
                  { key: 'all' as TimePeriod, label: 'All Time' },
                ]).map(({ key, label }) => (
                  <button key={key} onClick={() => setTimePeriod(key)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      timePeriod === key
                        ? 'bg-[var(--brand-surface-primary)] text-[var(--brand-accent-primary)] shadow-sm'
                        : 'text-[var(--brand-text-tertiary)] hover:text-[var(--brand-text-primary)]'
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* ── Summary Cards + Burn Rate ── */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {([
              { label: 'Total Requests', value: eu.totalEvents.toLocaleString() },
              { label: 'Input Tokens', value: eu.totalInputTokens.toLocaleString() },
              { label: 'Output Tokens', value: eu.totalOutputTokens.toLocaleString() },
              { label: 'Est. Cost', value: `$${totalCost.toFixed(4)}` },
            ]).map(({ label, value }) => (
              <div key={label} className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-primary)] p-4">
                <p className="text-xs text-[var(--brand-text-tertiary)] mb-1">{label}</p>
                <p className="text-xl font-semibold text-[var(--brand-text-primary)]">{value}</p>
              </div>
            ))}
            {/* Burn rate card */}
            <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-primary)] p-4">
              <p className="text-xs text-[var(--brand-text-tertiary)] mb-1 flex items-center gap-1">
                <ArrowTrendingUpIcon className="w-3 h-3" /> Projected Monthly
              </p>
              <p className="text-xl font-semibold text-[var(--brand-text-primary)]">${projectedMonthlyCost.toFixed(2)}</p>
              <p className="text-xs text-[var(--brand-text-tertiary)] mt-1">
                ~${avgDailyCost.toFixed(4)}/day avg ({days.length} day{days.length !== 1 ? 's' : ''} of data)
              </p>
            </div>
          </div>

          {/* ── Provider Cost Breakdown with Bars ── */}
          <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-primary)] p-5">
            <h3 className="text-sm font-semibold text-[var(--brand-text-secondary)] uppercase tracking-wider mb-4">
              Cost by Provider
            </h3>
            {Object.keys(eu.byProvider).length === 0 ? (
              <p className="text-sm text-[var(--brand-text-tertiary)]">No usage recorded yet</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(eu.byProvider)
                  .sort(([, a], [, b]) => b.cost - a.cost)
                  .map(([key, data]) => {
                    const pct = totalCost > 0 ? (data.cost / totalCost) * 100 : 0;
                    const colors: Record<string, string> = {
                      openai: 'bg-emerald-500',
                      anthropic: 'bg-orange-500',
                      moonshot: 'bg-purple-500',
                      ollama_chat: 'bg-blue-400',
                      'ollama-ai01': 'bg-cyan-400',
                    };
                    const barColor = colors[key] || 'bg-gray-400';
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-[var(--brand-text-primary)]">{key}</span>
                          <div className="flex items-center gap-4 text-xs text-[var(--brand-text-tertiary)]">
                            <span>{data.events} req</span>
                            <span>{data.tokens.toLocaleString()} tok</span>
                            <span className="font-medium text-[var(--brand-text-secondary)]">${data.cost.toFixed(4)}</span>
                            <span className="w-12 text-right">{pct.toFixed(1)}%</span>
                          </div>
                        </div>
                        <div className="h-2 rounded-full bg-[var(--brand-surface-secondary)] overflow-hidden">
                          <div className={`h-full rounded-full ${barColor} transition-all duration-500`} style={{ width: `${Math.max(pct, 0.5)}%` }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* ── Model Cost Comparison Table ── */}
          <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-primary)] overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--brand-border)]">
              <h3 className="text-sm font-semibold text-[var(--brand-text-secondary)] uppercase tracking-wider">
                Model Cost Comparison
              </h3>
            </div>
            {sortedModels.length === 0 ? (
              <p className="p-5 text-sm text-[var(--brand-text-tertiary)]">No usage recorded yet</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--brand-surface-secondary)]/50">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Model</th>
                      <th className="text-right px-4 py-2.5"><SortHeader field="events" label="Requests" /></th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Input Tok</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Output Tok</th>
                      <th className="text-right px-4 py-2.5"><SortHeader field="cost" label="Total Cost" /></th>
                      <th className="text-right px-4 py-2.5"><SortHeader field="efficiency" label="$/1K Out" /></th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Rate Card</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Source / Updated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--brand-border)]">
                    {sortedModels.map((m) => {
                      const costPct = totalCost > 0 ? (m.cost / totalCost) * 100 : 0;
                      const pricingAge = m.rate?.pricingUpdatedAt
                        ? Math.floor((Date.now() - new Date(m.rate.pricingUpdatedAt).getTime()) / 86400000)
                        : null;
                      return (
                        <tr key={m.key} className="hover:bg-[var(--brand-surface-secondary)]/30">
                          <td className="px-4 py-3">
                            <div className="font-medium text-[var(--brand-text-primary)]">{m.rate?.displayName || m.key}</div>
                            <div className="text-xs text-[var(--brand-text-tertiary)] mt-0.5">{m.providerKey}</div>
                          </td>
                          <td className="px-4 py-3 text-right text-[var(--brand-text-secondary)] tabular-nums">{m.events.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-[var(--brand-text-tertiary)] tabular-nums">{m.inputTokens.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-[var(--brand-text-tertiary)] tabular-nums">{m.outputTokens.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-medium text-[var(--brand-text-primary)] tabular-nums">${m.cost.toFixed(4)}</span>
                            <span className="text-xs text-[var(--brand-text-tertiary)] ml-1">({costPct.toFixed(1)}%)</span>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-[var(--brand-text-secondary)]">
                            {m.costPer1kOut > 0 ? `$${m.costPer1kOut.toFixed(4)}` : '—'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {m.rate?.costPer1kInput != null ? (
                              <div className="text-xs tabular-nums text-[var(--brand-text-tertiary)]">
                                <span className="text-green-600 dark:text-green-400">${m.rate.costPer1kInput.toFixed(4)}</span>
                                {' / '}
                                <span className="text-orange-600 dark:text-orange-400">${m.rate.costPer1kOutput?.toFixed(4)}</span>
                                <span className="text-[var(--brand-text-tertiary)]"> /1K</span>
                              </div>
                            ) : (
                              <span className="text-xs text-red-500">No rate</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {m.rate?.pricingUpdatedAt ? (
                              <div>
                                <div className="text-xs text-[var(--brand-text-tertiary)]">Registry DB</div>
                                <div className={`text-xs ${pricingAge != null && pricingAge > 30 ? 'text-red-500' : pricingAge != null && pricingAge > 7 ? 'text-amber-500' : 'text-green-600 dark:text-green-400'}`}>
                                  {new Date(m.rate.pricingUpdatedAt).toLocaleDateString()}
                                  {pricingAge != null && pricingAge > 0 && ` (${pricingAge}d)`}
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-red-500">Not configured</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Cost by Feature Surface ── */}
          <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-primary)] p-5">
            <h3 className="text-sm font-semibold text-[var(--brand-text-secondary)] uppercase tracking-wider mb-4">
              Cost by Feature
            </h3>
            {Object.keys(eu.byFeatureSurface).length === 0 ? (
              <p className="text-sm text-[var(--brand-text-tertiary)]">No usage recorded yet</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(eu.byFeatureSurface)
                  .sort(([, a], [, b]) => b.cost - a.cost)
                  .map(([key, data]) => {
                    const pct = totalCost > 0 ? (data.cost / totalCost) * 100 : 0;
                    const featureColors: Record<string, string> = {
                      chat: 'bg-blue-500',
                      chat_coordinated: 'bg-indigo-500',
                      agent: 'bg-purple-500',
                    };
                    return (
                      <div key={key}>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-[var(--brand-text-primary)]">
                            {featureSurfaceLabels[key] || key}
                          </span>
                          <div className="flex items-center gap-4 text-xs text-[var(--brand-text-tertiary)]">
                            <span>{data.events} req</span>
                            <span>{data.tokens.toLocaleString()} tok</span>
                            <span className="font-medium text-[var(--brand-text-secondary)]">${data.cost.toFixed(4)}</span>
                            <span className="w-12 text-right">{pct.toFixed(1)}%</span>
                          </div>
                        </div>
                        <div className="h-2 rounded-full bg-[var(--brand-surface-secondary)] overflow-hidden">
                          <div className={`h-full rounded-full ${featureColors[key] || 'bg-gray-400'} transition-all duration-500`} style={{ width: `${Math.max(pct, 0.5)}%` }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* ── Daily Cost Trend ── */}
          {days.length > 1 && (
            <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-primary)] p-5">
              <h3 className="text-sm font-semibold text-[var(--brand-text-secondary)] uppercase tracking-wider mb-4">
                Daily Cost Trend
              </h3>
              <div className="flex items-end gap-1 h-24">
                {(() => {
                  const maxCost = Math.max(...days.map(d => d.cost), 0.001);
                  return days.map((d) => {
                    const pct = (d.cost / maxCost) * 100;
                    return (
                      <div key={d.date} className="flex-1 flex flex-col items-center group relative">
                        <div className="w-full rounded-t bg-[var(--brand-accent-primary)]/70 hover:bg-[var(--brand-accent-primary)] transition-colors cursor-default"
                          style={{ height: `${Math.max(pct, 2)}%` }}
                          title={`${d.date}: $${d.cost.toFixed(4)} | ${d.events} req | ${(d.inputTokens + d.outputTokens).toLocaleString()} tok`}
                        />
                      </div>
                    );
                  });
                })()}
              </div>
              <div className="flex justify-between mt-1 text-xs text-[var(--brand-text-tertiary)]">
                <span>{days[0]?.date}</span>
                <span>{days[days.length - 1]?.date}</span>
              </div>
            </div>
          )}

          {/* ── Cost by User ── */}
          {eu.byUser.length > 0 && (
            <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-primary)] overflow-hidden">
              <div className="px-5 py-4 border-b border-[var(--brand-border)]">
                <h3 className="text-sm font-semibold text-[var(--brand-text-secondary)] uppercase tracking-wider">
                  Cost by User
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[var(--brand-surface-secondary)]/50">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">User</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Requests</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Tokens</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Cost</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">% of Total</th>
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Top Model</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--brand-border)]">
                    {eu.byUser.map((u) => {
                      const pct = totalCost > 0 ? (u.cost / totalCost) * 100 : 0;
                      return (
                        <tr key={u.userId} className="hover:bg-[var(--brand-surface-secondary)]/30">
                          <td className="px-4 py-3">
                            <div className="font-medium text-[var(--brand-text-primary)]">{u.displayName}</div>
                            <div className="text-xs text-[var(--brand-text-tertiary)]">{u.email}</div>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-[var(--brand-text-secondary)]">{u.events.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-[var(--brand-text-tertiary)]">{u.tokens.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium text-[var(--brand-text-primary)]">${u.cost.toFixed(4)}</td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-2">
                              <div className="w-16 h-1.5 rounded-full bg-[var(--brand-surface-secondary)] overflow-hidden">
                                <div className="h-full rounded-full bg-[var(--brand-accent-primary)]" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-xs tabular-nums text-[var(--brand-text-tertiary)] w-10 text-right">{pct.toFixed(1)}%</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-[var(--brand-text-tertiary)]">{u.topModel}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── Cost by Client/Matter ── */}
          <div className="rounded-xl border border-[var(--brand-border)] bg-[var(--brand-surface-primary)] overflow-hidden">
            <div className="px-5 py-4 border-b border-[var(--brand-border)]">
              <h3 className="text-sm font-semibold text-[var(--brand-text-secondary)] uppercase tracking-wider">
                Cost by Client / Matter
              </h3>
            </div>
            {eu.byMatter.length === 0 ? (
              <div className="p-5">
                <p className="text-sm text-[var(--brand-text-tertiary)]">
                  No matter-bound AI usage recorded yet. Costs will appear here when users interact with AI in matter-scoped sessions.
                </p>
                <div className="mt-3 flex items-center gap-4">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="w-3 h-3 rounded-full bg-gray-300 dark:bg-gray-600" />
                    <span className="text-[var(--brand-text-tertiary)]">General / Unbound: <span className="font-medium text-[var(--brand-text-primary)]">${totalCost.toFixed(4)}</span></span>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-[var(--brand-surface-secondary)]/50">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Client</th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Matter</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Requests</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Tokens</th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-[var(--brand-text-tertiary)] uppercase tracking-wider">Cost</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[var(--brand-border)]">
                      {eu.byMatter.map((m) => (
                        <tr key={m.matterId} className="hover:bg-[var(--brand-surface-secondary)]/30">
                          <td className="px-4 py-3 font-medium text-[var(--brand-text-primary)]">{m.clientName}</td>
                          <td className="px-4 py-3">
                            <div className="text-[var(--brand-text-secondary)]">{m.matterNumber}</div>
                            <div className="text-xs text-[var(--brand-text-tertiary)] truncate max-w-xs">{m.matterTitle}</div>
                          </td>
                          <td className="px-4 py-3 text-right tabular-nums text-[var(--brand-text-secondary)]">{m.events.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-[var(--brand-text-tertiary)]">{m.tokens.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right tabular-nums font-medium text-[var(--brand-text-primary)]">${m.cost.toFixed(4)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Unbound summary */}
                <div className="px-5 py-3 border-t border-[var(--brand-border)] bg-[var(--brand-surface-secondary)]/30 flex items-center justify-between">
                  <span className="text-xs text-[var(--brand-text-tertiary)]">General / Unbound usage (not attributed to a matter)</span>
                  <span className="text-sm font-medium text-[var(--brand-text-primary)] tabular-nums">${eu.unboundCost.toFixed(4)}</span>
                </div>
              </>
            )}
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default AIModelGovernancePanel;
